"use client";
import { ArrowLeft, ArrowRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import Image from "next/image";

const DESKTOP_STEP = 450;
const MOBILE_STEP = 239;

const WhatSetsUsApart = () => {
  const [hover, setHover] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);

  const [index, setIndex] = useState(0);

  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const updateViewport = () => setIsDesktop(window.innerWidth >= 768);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const step = isDesktop ? DESKTOP_STEP : MOBILE_STEP;

  const handleNext = () => {
    setIndex((prev) =>
      prev < WHAT_SETS_US_APART.length - 1 ? prev + 1 : prev
    );
  };

  const handlePrevious = () => {
    setIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const WHAT_SETS_US_APART = [
    {
      title: "Internationally accredited curriculum",
      description: `Our courses are offered in partnership with the University of Bedfordshire. Explore our university and the strength of this academic partnership.`,
      link: "https://www.beds.ac.uk/about-us/our-university/",
      redirect: true,
      image: "/img/uni/uob.png",
    },
    {
      title: "Seed Funding Up to 1 Crore",
      description:
        "Our students are supported with seed funding for entrepreneurship and innovation. Explore how our students can become entrepreneurs.",
      link: "/about#upstart",
      redirect: false,
      image: "/DSC06098.jpg",
    },
    {
      title: "Lead by Proven IT Experts and Academician",
      description:
        "Our college is lead by proven IT academician and IT entrepreneur. Explore about our industry partnership",
      link: "/about/collaboration-and-training",
      redirect: false,
      image: "/teams/Riwaj.jpg",
    },
    {
      title: "Easy Transfer to UK",
      description:
        "Our students can seamlessly transfer their credits to the UK. Explore the credit transfer process.",
      link: "/about/about-University of Bedfordshire#uk",
      redirect: false,
      image: "/easy-transfer-to-uk.png",
    },
  ];

  const lastIndex = WHAT_SETS_US_APART.length - 1;

  return (
    <section
      className={cn(
        `h-[558px] `,
        `xl:float-none xl:m-[2.5rem_auto_5rem] xl:p-[0_0_5rem] `,
        `md:m-[1.875rem_0] md:p-0 `,
        `box-border clear-both float-left m-[1.5625rem_0] max-w-[100vw] 
        overflow-hidden p-[0_1.25rem_3.125rem] relative w-full z-0`
      )}
    >
      <header
        className={cn(
          `mt-0 float-left bg-white z-0`,
          `xl:max-w-[24.875rem] md:absolute md:z-[1] md:mt-[-1.25rem] md:min-h-full md:w-full md:max-w-[20rem] `
        )}
      >
        <h3
          className={cn(
            `xl:max-w-[21.5625rem] `,
            `md:text-[2.25rem] md:leading-[4.375rem] md:m-[0.3125rem_0_0_2.5rem] md:relative md:max-w-[16.875rem] md:z-[2]`,
            `text-[1.75rem] leading-[2.25rem] pt-[0.25rem]`
          )}
        >
          What sets us apart?
        </h3>
        <div
          className={cn(
            `md:block md:mb-[1.875rem] md:ml-[2.5rem] md:relative md:z-[2] md:w-[15rem] `,
            `text-base leading-[1.5rem]`
          )}
        >
        </div>
        <div
          className={cn(
            `md:relative md:z-[2] md:ml-[2.1875rem] `,
            `text-[0.6875rem] leading-[1.125rem] tracking-[0.0625rem] text-black [text-decoration:none] uppercase
            outline-none bg-white overflow-visible m-[0_0_0.625rem_-0.3125rem] transition-[all_0.35s_ease-out]
            table relative p-0 b-0 [backface-visibility:hidden] left-[0.3125rem]
            `,
            `before:absolute before:inset-0 before:z-0 
                before:bg-[url('/svgs/dot-black.svg')] before:bg-[length:0.1875rem] 
                before:[transform:translate(-0.625rem,0.625rem)] before:[transition:all_0.35s_ease-out] before:ease-out`,
            {
              "text-primary-red border-primary-red transition-[all_0.15s_ease] left-0 before:transition-[all_0.15s_ease] before:transform before:translate-x-[-0.3125rem] before:translate-y-[0.3125rem] before:bg-none before:bg-primary-red":
                hoveredButton,
            }
          )}
        >
          <button
            aria-label="pre"
            onClick={handlePrevious}
            disabled={index === 0}
            onMouseEnter={() => index !== 0 && setHoveredButton("prev")}
            onMouseLeave={() => setHoveredButton(null)}
            className={`
    relative cursor-pointer w-[3.875rem] h-[3.125rem]
    outline-none float-left p-0 z-[2]
    border-[0.0625rem] flex items-center justify-center
    transition-all duration-200
    ${index === 0
                ? "bg-white border-neutral-300 cursor-not-allowed"
                : hoveredButton === "prev"
                  ? "bg-primary-red border-primary-red"
                  : "bg-white border-[#272727]"
              }
  `}
          >
            <ArrowLeft
              strokeWidth={1}
              className={`${index === 0
                ? "text-neutral-400"
                : hoveredButton === "prev"
                  ? "text-white"
                  : "text-black"
                }`}
            />
          </button>

          <button
            aria-label="next"
            onClick={handleNext}
            disabled={index === lastIndex}
            onMouseEnter={() => index !== lastIndex && setHoveredButton("next")}
            onMouseLeave={() => setHoveredButton(null)}
            className={`
    relative cursor-pointer w-[3.875rem] h-[3.125rem]
    outline-none float-left p-0 z-[2]
    border-[0.0625rem] border-l-0 flex items-center justify-center
    transition-all duration-200
    ${index === lastIndex
                ? "bg-white border-neutral-300 cursor-not-allowed"
                : hoveredButton === "next"
                  ? "bg-primary-red border-primary-red"
                  : "bg-white border-[#272727]"
              }
  `}
          >
            <ArrowRight
              strokeWidth={1}
              className={`${index === lastIndex
                ? "text-neutral-400"
                : hoveredButton === "next"
                  ? "text-white"
                  : "text-black"
                }`}
            />
          </button>
        </div>
      </header>

      <div className={``}>
        <ul
          className={cn(
            `xl:mt-[-1.25rem] xl:ml-[26.75rem] `,
            `md:items-end md:ml-[21.125rem] md:mt-[5.875rem] `,
            `p-0 m-0 clear-both mt-[2.8125rem] float-left flex relative`
          )}
          style={{
            transform: `translateX(-${index * step}px)`,
            transition: "transform 300ms",
          }}
        >
          <div
            className={`[position:absolute_!important] overflow-hidden [clip:rect(1px,1px,1px,1px)] size-[1px] [word-wrap:normal]`}
            aria-live="polite"
            arial-atomic="true"
            aria-hidden="true"
            style={{
              clip: "rect(1px,1px,1px,1px)",
            }}
          >
            <p>
              Now showing {index + 1} out of {WHAT_SETS_US_APART.length}
            </p>
          </div>

          {WHAT_SETS_US_APART.map((item, idx) => {
            return (
              <li
                key={item.title.toLocaleLowerCase()}
                className={cn(
                  `xl:min-w-[26.25rem] xl:basis-[26.25rem]
                   min-w-[16.75rem] md:basis-[16.75rem]
                   xl:mr-[1.875rem] md:mr-[1.125rem] md:mb-0 pl-0 [list-style:none] 
                   relative basis-[14.375rem] mr-[0.53125rem]
                    `,
                  {
                    "brightness-[120%]": idx == 0,
                    "brightness-[120%]": idx == 2,
                  }
                )}
              >
                <figure className={`m-0`}>
                  <div className={`md:mb-[1.125rem] table mb-[0.9375rem]`}>
                    <Image
                      className={cn(
                        `xl:size-[26.25rem] md:size-[16.75rem] size-[14.375rem] bg-no-repeat relative bg-[#AACCEE] object-cover
                        before:absolute before:top-0 before:left-0 before:size-full before:contrast-[160%] before:z-0 before:pointer-events-none before:opacity-0 before:transition-[opacity_0.35s_ease-out]`
                      )}
                      src={item.image}
                      alt={item.title}
                      width={420}
                      priority={idx === 0}
                      height={420}
                      unoptimized={item.title === "Lead by Proven IT Experts and Academician"}
                    />
                  </div>
                </figure>
                <figcaption
                  className={cn(
                    `md:text-[0.875rem] md:tracking-[0.09375rem]`,
                    `md:text-[0.6875rem] md:leading-4 tracking-[0.08625rem] transition-[all_0.3s]`,
                    `h-[3.75rem] text-[0.875rem] leading-4 tracking-[0.07813rem] relative`,
                    {
                      "md:hidden md:opacity-0": index > idx,
                    }
                  )}
                >
                  <div className={`uppercase font-bold`} aria-hidden="true">
                    {item.title}
                  </div>
                  <div className={``} aria-hidden="true">
                    <p
                      className={`h-[3.75rem] text-sm leading-4 tracking-[0.07813rem] relative 
                            xl:mb-6 md:mb-[1.9375rem] md:text-base`}
                    >
                      <Link
                        target={item.redirect ? "_blank" : "_self"}
                        href={item.link || "#"}
                        className={`decoration-[#A31F34] underline text-sm leading-4 tracking-[0.07813rem] hover:text-[#A31F34] focus:text-[#A31F34]`}
                      >
                        {item.description}
                      </Link>
                    </p>
                  </div>
                </figcaption>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default WhatSetsUsApart;
